import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Copy, Users, Coins, DollarSign, Share2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ReferralLeaderboardPanel from "../components/ReferralLeaderboardPanel";

/**
 * ReferralsPage — user dashboard for referral invites.
 *
 * - Shows the user's unique code + one-click share link
 * - Lists every invite (pending/approved/denied) with the invitee's email
 * - Summary totals: pending, approved, points earned, cash pending/paid
 */
export default function ReferralsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/referrals/mine");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); else navigate("/login"); }, [user, navigate]);

  const inviteUrl = data ? `${window.location.origin}/register?ref=${data.code}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(inviteUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  const share = async () => {
    const text = `Join me on Tap IN TV Network — entertainment built for creators. Use my link and I get credit: ${inviteUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Join Tap IN TV", text, url: inviteUrl }); return; } catch {}
    }
    copy();
    toast.success("Link copied — paste anywhere to invite");
  };

  if (loading || !data) return <div className="min-h-screen bg-[#05060a] text-white flex items-center justify-center">Loading…</div>;

  const s = data.summary;

  return (
    <div data-testid="referrals-page" className="min-h-screen bg-[#05060a] text-white">
      <div className="max-w-4xl mx-auto px-6 pt-28 md:pt-32 pb-6">
        <div className="flex items-center gap-3 mb-2">
          <Gift className="w-8 h-8 text-tv-accent" />
          <h1 className="font-display font-black text-3xl">Invite & Earn</h1>
        </div>
        <p className="text-white/60 mb-6">
          Share your link. Every friend who joins gets <strong>you</strong> {data.rules.points_per_referral.toLocaleString()} points once an admin approves the signup.{" "}
          {s.cash_eligible && <span className="text-emerald-300">You're cash-eligible — you earn <strong>${data.rules.cash_per_referral_usd.toFixed(2)} cash</strong> per approved invite instead of points.</span>}
        </p>

        {/* Share card */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-3 mb-6" data-testid="referral-share-card">
          <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Your invite link</div>
          <div className="flex items-center gap-2">
            <code data-testid="referral-url" className="flex-1 min-w-0 bg-black/50 border border-white/10 rounded px-3 py-2 text-sm font-mono text-tv-accent truncate">{inviteUrl}</code>
            <button onClick={copy} data-testid="referral-copy" className="bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-black text-sm px-3 py-2 rounded inline-flex items-center gap-1.5"><Copy className="w-4 h-4" /> {copied ? "Copied!" : "Copy"}</button>
            <button onClick={share} data-testid="referral-share" className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-sm px-3 py-2 rounded inline-flex items-center gap-1.5"><Share2 className="w-4 h-4" /> Share</button>
          </div>
          <div className="text-white/50 text-xs">Code: <span className="text-white font-mono">{data.code}</span></div>
        </div>

        {/* Summary grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="referral-summary">
          <Stat icon={<Users className="w-5 h-5" />} label="Total invited" value={s.total} />
          <Stat icon={<Users className="w-5 h-5 text-amber-300" />} label="Pending" value={s.pending} />
          <Stat icon={<Coins className="w-5 h-5 text-tv-accent" />} label="Points earned" value={s.points_earned.toLocaleString()} />
          <Stat
            icon={<DollarSign className="w-5 h-5 text-emerald-300" />}
            label="Cash earned"
            value={`$${(s.cash_earned_usd || 0).toFixed(2)}`}
            sub={s.cash_pending_usd > 0 ? `$${s.cash_pending_usd.toFixed(2)} pending payout` : null}
          />
        </div>

        {/* Referrals list */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-5">
          <h2 className="font-black text-lg mb-3">Your invites</h2>
          {data.referrals.length === 0 ? (
            <div className="text-white/50 text-sm py-6 text-center">
              No invites yet. Share your link above to start earning.
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="referral-list">
              {data.referrals.map((r) => (
                <div key={r.id} data-testid={`referral-row-${r.id}`} className="flex items-center gap-3 bg-white/5 rounded p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{r.invitee_name || r.invitee_email || "New Tap IN user"}</div>
                    <div className="text-white/50 text-xs">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  {r.status === "approved" && (
                    <span className="text-emerald-300 text-xs font-black">
                      {r.reward_kind === "cash" ? `+$${r.awarded_cash_usd.toFixed(2)}` : `+${r.awarded_points} pts`}
                    </span>
                  )}
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                    r.status === "pending" ? "bg-amber-500/25 text-amber-200" :
                    r.status === "approved" ? "bg-emerald-500/25 text-emerald-200" :
                    "bg-red-500/25 text-red-200"
                  }`}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <ReferralLeaderboardPanel />
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-3">
      <div className="flex items-center gap-1 text-white/60 text-[10px] uppercase tracking-wider font-black">{icon}{label}</div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
      {sub && <div className="text-white/50 text-[10px]">{sub}</div>}
    </div>
  );
}
